import React, {ReactElement, useState} from "react";
import {Dialog} from "@headlessui/react";
import {FormattedMessage} from "react-intl";
import {ReactComponent as CloseIcon} from "../../Icons/close.svg";
import {LocalImage} from "./LocalImagePicker";
import ReactCrop, {
    Crop
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {Spinner} from "../Spinner";
import {SecondaryButton} from "../SecondaryButton";
import {PrimaryButton} from "../PrimaryButton";
import {getImageSize} from "../../../lib/util";


export interface ImageResizerProps {
    image: LocalImage
    aspect?: number
    onClose: ()=>void
    onResize: (img: LocalImage)=>void
}

const cropImage = async (image: HTMLImageElement, crop: Crop): Promise<File|undefined> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height,
    );

    const b64 = canvas.toDataURL('image/png');
    const res: Response = await fetch(b64);
    const blob: Blob = await res.blob();
    return new File([blob], "", { type: 'image/png' });
};

export function ImageResizer(props: ImageResizerProps): ReactElement {
    const [crop, setCrop] = useState<Crop>();
    const [image, setImage] = useState<HTMLImageElement|null>(null);

    const pickImage = async () => {
        const croppedFile = await cropImage(image!, crop!);
        if (!croppedFile) return;
        const url = URL.createObjectURL(croppedFile);
        const imageSize = await getImageSize(url);
        const img = {
            dataUrl: url,
            binaryData: croppedFile,
            stats: {
                fileSize: croppedFile.size,
                width: imageSize.width,
                height: imageSize.height
            }
        };
        props.onResize(img);
    };

    const pending = !image;

    return <Dialog
        open={true}
        onClose={props.onClose}
        className="fixed z-modal inset-0 overflow-y-auto"
    >
        <div className="flex items-center justify-center min-h-screen">
            <Dialog.Overlay className="fixed inset-0 bg-text opacity-60"/>

            <div className="relative flex flex-col w-2/3 max-w-lg h-2/3 mx-auto gap-5
                                py-4 px-5 bg-white rounded-lg">
                <div className="flex-none flex items-center">
                    <Dialog.Title className="flex-auto font-barlow text-lg font-semibold">
                        <FormattedMessage id="editor.image.upload.resize"/>
                    </Dialog.Title>
                    <button className="flex-none"
                            onClick={props.onClose}>
                        <CloseIcon className="inline-block w-5 h-5"/>
                    </button>
                </div>

                <ReactCrop
                    crop={crop}
                    onChange={setCrop}
                    aspect={props.aspect}
                >
                    <img
                        alt="Preview"
                        src={props.image.dataUrl}
                        onLoad={e=>setImage(e.currentTarget)}
                    />
                </ReactCrop>

                <div className="flex-none flex gap-2 justify-end items-center">
                    {pending && (
                        <Spinner className="flex-none w-8 h-8 inline-block"/>
                    )}

                    <SecondaryButton onClick={props.onClose}>
                        <FormattedMessage id="editor.image.upload.cancel"/>
                    </SecondaryButton>

                    <PrimaryButton
                        onClick={pickImage}
                        disabled={pending || !crop}
                    >
                        <FormattedMessage id="editor.image.picker.chose"/>
                    </PrimaryButton>
                </div>
            </div>
        </div>
    </Dialog>
}